import React, { useState, useEffect } from 'react';
import { Spin, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import dayjs from 'dayjs';

const GOLD = '#C8922A';
const RED = '#C0392B';
const GRN = '#2E7D4F';
// const BLUE = '#2B5CE6';
const INK = '#1A1612';
const JPY_CNY_FALLBACK = 0.043;

function useCountdown(endAt) {
  const [h, setH] = useState('00');
  const [m, setM] = useState('00');
  const [s, setS] = useState('00');
  const [secs, setSecs] = useState(0);
  const [totalSecs, setTotalSecs] = useState(1);
  useEffect(() => {
    if (!endAt) return;
    const startDiff = dayjs(endAt).diff(dayjs(), 'second');
    if (startDiff > 0) setTotalSecs(startDiff);
    const update = () => {
      const diff = Math.max(0, dayjs(endAt).diff(dayjs(), 'second'));
      setSecs(diff);
      setH(String(Math.floor(diff / 3600)).padStart(2, '0'));
      setM(String(Math.floor((diff % 3600) / 60)).padStart(2, '0'));
      setS(String(diff % 60).padStart(2, '0'));
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [endAt]);
  const pct = totalSecs > 0 ? Math.max(0, Math.min(100, (secs / totalSecs) * 100)) : 0;
  const urgent = secs > 0 && secs < 600;
  return { h, m, s, secs, pct, urgent };
}

function formatJPY(val) {
  if (val == null) return '--';
  return '¥' + Number(val).toLocaleString('ja-JP');
}


export default function Auction() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [lots, setLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickBidVisible, setQuickBidVisible] = useState(false);
  const [quickBidValue, setQuickBidValue] = useState('');
  const [bidHistory, setBidHistory] = useState([]);
  const [engineStatus, setEngineStatus] = useState(null);
  const [JPY_CNY, setJpyCny] = useState(JPY_CNY_FALLBACK);
  useEffect(() => {
    api.get('/exchange-rates/current').then(({ data }) => {
      const r = data?.rate; if (r && r > 0) setJpyCny(Number(r));
    }).catch(() => {});
  }, []);

  // Fetch sessions
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const { data } = await api.get('/sessions');
        const list = data.data || data || [];
        setSessions(list);
        if (list.length > 0) setSelectedSessionId(list[0].id);
      } catch (err) {
        console.error('Failed to fetch sessions', err);
        setSessions([]);
      }
    };
    fetchSessions();
  }, []);

  // Fetch lots for selected session
  useEffect(() => {
    if (!selectedSessionId) return;
    const fetchLots = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/lots', { params: { session_id: selectedSessionId, page_size: 100 } });
        const list = data.data || [];
        setLots(list);
        if (list.length > 0) setSelectedLot(list[0]);
      } catch (err) {
        console.error('Failed to fetch lots', err);
        setLots([]);
        setSelectedLot(null);
      } finally {
        setLoading(false);
      }
    };
    fetchLots();
  }, [selectedSessionId, sessions]);

  const currentSession = sessions.find(s => s.id === selectedSessionId) || sessions[0] || {};
  const cd = useCountdown(currentSession.end_at);
  const currentPrice = selectedLot?.current_price || 0;
  const cnyCurrent = Math.round(currentPrice * JPY_CNY);
  const myMaxBid = selectedLot?.max_bid || 0;
  const bidRoom = myMaxBid > 0 ? myMaxBid - currentPrice : 0;

  // Fetch bid history & engine status for selected lot
  useEffect(() => {
    if (!selectedLot?.id) { setBidHistory([]); setEngineStatus(null); return; }
    const fetchBidInfo = async () => {
      try {
        const { data } = await api.get(`/lots/${selectedLot.id}/bids`);
        setBidHistory(data.data || data || []);
      } catch { setBidHistory([]); }
      try {
        const { data } = await api.get(`/engine/status`, { params: { lot_id: selectedLot.id } });
        setEngineStatus(data.data || data || null);
      } catch { setEngineStatus(null); }
    };
    fetchBidInfo();
  }, [selectedLot?.id]);

  const liveLots = lots.filter(l => l.status === 'live').length;
  const doneLots = lots.filter(l => l.status === 'done').length;
  const myMandates = lots.filter(l => l.has_mandate).length;

  const getBadge = (status, hasMine) => {
    if (status === 'live') return { text: 'LIVE', style: { background: 'rgba(46,125,79,.2)', color: '#5FC98A' } };
    if (status === 'soon') return { text: t('session.upcoming'), style: { background: 'rgba(200,146,42,.15)', color: GOLD } };
    if (status === 'done') return { text: t('browse.ended'), style: { background: 'rgba(255,255,255,.06)', color: 'rgba(255,255,255,.3)' } };
    if (hasMine) return { text: t('auction.mine'), style: { background: 'rgba(43,92,230,.2)', color: '#7BA7F7' } };
    return null;
  };

  const getIcon = (cat) => {
    const map = { watch: '⌚', jewelry: '💍', bag: '👜' };
    return map[cat] || '📦';
  };

  const handleQuickBid = () => {
    const val = Number(quickBidValue);
    if (!val || val <= currentPrice) {
      message.error(t('auction.bidFailed'));
      return;
    }
    message.success(t('auction.authPriceUpdated', { price: val.toLocaleString() }));
    setQuickBidVisible(false);
    setQuickBidValue('');
  };

  return (
    <div style={{ fontFamily: 'var(--sans,sans-serif)', background: INK, color: '#fff', fontSize: 14, height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* -- Topbar -- */}
      <div style={{
        height: 52, background: 'rgba(255,255,255,.04)', borderBottom: '1px solid rgba(255,255,255,.08)',
        display: 'flex', alignItems: 'center', padding: '0 18px', gap: 12, flexShrink: 0,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontFamily: 'var(--serif,serif)', fontSize: 16, fontWeight: 600, letterSpacing: 3 }}>{t('brand.name')}</span>
          <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(255,255,255,.3)', letterSpacing: 2 }}>YunChuan · LIVE</span>
        </div>

        {/* Session selector */}
        <div style={{
          marginLeft: 16, display: 'flex', alignItems: 'center',
          background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 4, overflow: 'hidden',
        }}>
          <span style={{ padding: '6px 12px', fontSize: 11, color: 'rgba(255,255,255,.4)', borderRight: '1px solid rgba(255,255,255,.08)' }}>{t('browse.session')}</span>
          <select
            value={selectedSessionId}
            onChange={e => setSelectedSessionId(e.target.value)}
            style={{
              background: 'transparent', border: 'none', color: '#fff',
              fontSize: 12, fontWeight: 500, padding: '6px 10px', cursor: 'pointer', outline: 'none',
              appearance: 'none', WebkitAppearance: 'none',
            }}
          >
            {sessions.map(s => (
              <option key={s.id} value={s.id} style={{ background: INK, color: '#fff' }}>
                {s.house_name} · {s.name}
              </option>
            ))}
          </select>
          <span style={{ padding: '6px 10px', color: 'rgba(255,255,255,.4)', pointerEvents: 'none', marginLeft: -24 }}>▾</span>
        </div>

        {/* Live badge */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 5,
          fontFamily: 'monospace', fontSize: 10, color: '#5FC98A',
          background: 'rgba(46,125,79,.15)', border: '1px solid rgba(46,125,79,.25)',
          borderRadius: 3, padding: '4px 10px', letterSpacing: 1,
        }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#5FC98A', animation: 'blink 1s infinite' }} />
          LIVE
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate('/mandates')} style={{
            padding: '5px 12px', border: '1px solid rgba(255,255,255,.15)', borderRadius: 3,
            fontSize: 12, color: 'rgba(255,255,255,.6)', cursor: 'pointer', background: 'transparent',
          }}>← {t('nav.mandates')}</button>
          <button onClick={() => navigate('/auction-hub')} style={{
            padding: '5px 12px', border: '1px solid rgba(255,255,255,.15)', borderRadius: 3,
            fontSize: 12, color: 'rgba(255,255,255,.6)', cursor: 'pointer', background: 'transparent',
          }}>{t('browse.title')}</button>
        </div>
      </div>

      {/* -- Main 3-column layout -- */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 320px 280px', overflow: 'hidden' }}>
        {/* -- Left: Lot panel -- */}
        <div style={{ borderRight: '1px solid rgba(255,255,255,.06)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Session bar */}
          <div style={{
            padding: '10px 18px', background: 'rgba(255,255,255,.03)',
            borderBottom: '1px solid rgba(255,255,255,.06)',
            display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
          }}>
            <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(255,255,255,.4)', letterSpacing: 2 }}>
              {(currentSession.house_name || '').toUpperCase()}
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', flex: 1 }}>{currentSession.name}</span>
            <span style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 2,
              background: 'rgba(43,92,230,.2)', color: '#7BA7F7', fontFamily: 'monospace',
            }}>
              {getIcon(currentSession.type)} {currentSession.type === 'watch' ? t('browse.watches') : currentSession.type === 'jewelry' ? t('browse.jewelry') : t('auction.mixed')}
            </span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,.35)' }}>
              {lots.length} {t('common.items')} · {t('auction.traded')} {doneLots}
            </span>
          </div>

          {/* Lot list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            ) : (
              lots.map(lot => {
                const isActive = selectedLot?.id === lot.id;
                const isDone = lot.status === 'done';
                const badge = getBadge(lot.status, lot.has_mandate);
                return (
                  <div
                    key={lot.id}
                    onClick={() => setSelectedLot(lot)}
                    style={{
                      display: 'flex', gap: 10, padding: '10px', borderRadius: 4,
                      cursor: 'pointer', border: '1px solid transparent', marginBottom: 4,
                      background: isActive ? 'rgba(200,146,42,.08)' : 'transparent',
                      borderColor: isActive ? 'rgba(200,146,42,.2)' : 'transparent',
                      opacity: isDone ? 0.45 : 1,
                      transition: 'background .12s',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,.04)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(255,255,255,.3)', width: 28, flexShrink: 0, marginTop: 2 }}>
                      {String(lot.lot_number || lot.id).padStart(2, '0')}
                    </span>
                    <span style={{ fontSize: 20, flexShrink: 0 }}>{getIcon(lot.category)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 12, fontWeight: 500, color: '#fff',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 3,
                      }}>
                        {lot.title_zh || lot.title_ja || '--'}
                      </div>
                      <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(255,255,255,.5)' }}>
                        {formatJPY(lot.current_price)} JPY
                        {lot.has_mandate && <span style={{ color: GOLD, fontSize: 10 }}> · {t('auction.myMandate')}</span>}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      {badge && (
                        <span style={{
                          fontSize: 10, padding: '2px 7px', borderRadius: 2, fontFamily: 'monospace',
                          ...badge.style,
                          ...(lot.status === 'live' ? { animation: 'blink 1.5s infinite' } : {}),
                        }}>{badge.text}</span>
                      )}
                      {lot.has_mandate && lot.status !== 'done' && (
                        <div style={{
                          fontSize: 10, padding: '2px 7px', borderRadius: 2, marginTop: 4,
                          background: 'rgba(43,92,230,.2)', color: '#7BA7F7',
                        }}>{t('auction.mandateActive')}</div>
                      )}
                      {lot.has_mandate && lot.max_bid > 0 && (
                        <div style={{ fontFamily: 'monospace', fontSize: 10, color: GOLD, marginTop: 3 }}>
                          {t('auction.authPrice')}¥{Math.round(lot.max_bid / 10000)}{t('auction.tenThousand')}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* -- Center: Bid panel -- */}
        <div style={{ borderRight: '1px solid rgba(255,255,255,.06)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Countdown */}
          <div style={{ background: 'rgba(255,255,255,.03)', borderBottom: '1px solid rgba(255,255,255,.06)', padding: '16px 18px', flexShrink: 0 }}>
            <div style={{
              fontSize: 10, color: 'rgba(255,255,255,.35)', letterSpacing: 2, fontFamily: 'monospace',
              marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span>{t('auction.timeRemaining')}</span>
              <span style={{ fontSize: 10, opacity: 0.6 }}>{currentSession.end_at ? dayjs(currentSession.end_at).format('HH:mm') + ' JST' : ''}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 8 }}>
              {[
                { val: cd.h, unit: t('browse.hours') },
                { val: cd.m, unit: t('browse.minutes') },
                { val: cd.s, unit: t('browse.seconds') },
              ].map((b, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span style={{ fontFamily: 'monospace', fontSize: 28, color: 'rgba(255,255,255,.2)', marginBottom: 14 }}>:</span>}
                  <div style={{ textAlign: 'center' }}>
                    <span style={{
                      fontFamily: 'monospace', fontSize: 36, fontWeight: 500, color: cd.urgent ? RED : '#fff',
                      lineHeight: 1, minWidth: 54, display: 'block',
                      ...(cd.urgent ? { animation: 'urgentpulse .5s infinite alternate' } : {}),
                    }}>{b.val}</span>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,.25)', letterSpacing: 1, marginTop: 3 }}>{b.unit}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
            {/* Progress bar */}
            <div style={{ height: 3, background: 'rgba(255,255,255,.08)', borderRadius: 2, overflow: 'hidden', marginBottom: 10 }}>
              <div style={{ height: '100%', background: `linear-gradient(90deg,${GRN},${GOLD})`, borderRadius: 2, width: `${cd.pct}%`, transition: 'width 1s linear' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
              <div style={{ background: 'rgba(255,255,255,.04)', borderRadius: 3, padding: '7px 10px' }}>
                <div style={{ color: 'rgba(255,255,255,.3)', marginBottom: 3, fontSize: 10 }}>{t('session.startTime')}</div>
                <div style={{ fontFamily: 'monospace', fontWeight: 500 }}>{currentSession.start_at ? dayjs(currentSession.start_at).format('HH:mm') + ' JST' : '--'}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,.04)', borderRadius: 3, padding: '7px 10px' }}>
                <div style={{ color: 'rgba(255,255,255,.3)', marginBottom: 3, fontSize: 10 }}>{t('session.endTime')}</div>
                <div style={{ fontFamily: 'monospace', fontWeight: 500 }}>{currentSession.end_at ? dayjs(currentSession.end_at).format('HH:mm') + ' JST' : '--'}</div>
              </div>
            </div>
          </div>

          {/* Price block */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,.06)', flexShrink: 0 }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.35)', letterSpacing: 2, fontFamily: 'monospace', marginBottom: 8 }}>{t('auction.currentBid')}</div>
            <div style={{ fontFamily: 'monospace', fontSize: 38, fontWeight: 500, color: '#fff', lineHeight: 1, marginBottom: 4 }}>
              {currentPrice ? Number(currentPrice).toLocaleString() : '--'}
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.4)' }}>≈ ¥{cnyCurrent.toLocaleString()} CNY</div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'monospace', fontSize: 11, color: GOLD, marginTop: 5 }}>
              ↑ +¥30,000
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 10 }}>
              <div style={{ background: 'rgba(255,255,255,.04)', borderRadius: 3, padding: '6px 9px' }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,.3)', marginBottom: 2 }}>{t('auction.lot')}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedLot?.title_zh?.slice(0, 18) || '--'}
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,.04)', borderRadius: 3, padding: '6px 9px' }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,.3)', marginBottom: 2 }}>{t('lotDetail.bidCount')}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 500 }}>{bidHistory.length}</div>
              </div>
              {myMaxBid > 0 && (
                <>
                  <div style={{ background: 'rgba(255,255,255,.04)', borderRadius: 3, padding: '6px 9px' }}>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,.3)', marginBottom: 2 }}>{t('auction.myBid')}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 500, color: GOLD }}>{formatJPY(myMaxBid)}</div>
                  </div>
                  <div style={{
                    background: 'rgba(255,255,255,.04)', borderRadius: 3, padding: '8px 10px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,.3)', marginBottom: 2 }}>{t('auction.bidRoom')}</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 500, color: bidRoom > 0 ? '#5FC98A' : RED }}>
                        {formatJPY(Math.max(0, bidRoom))}
                      </div>
                    </div>
                    <button
                      onClick={() => setQuickBidVisible(!quickBidVisible)}
                      style={{
                        padding: '5px 12px', background: 'rgba(200,146,42,.15)',
                        border: '1px solid rgba(200,146,42,.3)', borderRadius: 3,
                        fontSize: 11, color: GOLD, cursor: 'pointer', fontFamily: 'monospace', whiteSpace: 'nowrap',
                      }}
                    >⬆ {t('auction.adjustAuth')}</button>
                  </div>
                </>
              )}
            </div>
            {/* Quick bid panel */}
            {quickBidVisible && (
              <div style={{
                marginTop: 10, padding: '12px 14px',
                background: 'rgba(200,146,42,.08)', border: '1px solid rgba(200,146,42,.25)', borderRadius: 4,
              }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', letterSpacing: 1, fontFamily: 'monospace', marginBottom: 8 }}>⬆ {t('auction.adjustAuthTemp')}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', whiteSpace: 'nowrap' }}>{t('auction.newAuthPrice')}</div>
                  <input
                    type="number" step="10000" value={quickBidValue}
                    onChange={e => setQuickBidValue(e.target.value)}
                    placeholder={t('auction.jpyPlaceholder')}
                    style={{
                      flex: 1, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.2)',
                      borderRadius: 3, padding: '8px 10px', color: '#fff', fontFamily: 'monospace', fontSize: 14, outline: 'none',
                    }}
                  />
                  <button onClick={handleQuickBid} style={{
                    padding: '8px 16px', background: GOLD, color: '#fff', border: 'none', borderRadius: 3,
                    fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>{t('common.confirm')}</button>
                  <button onClick={() => setQuickBidVisible(false)} style={{
                    padding: '8px 10px', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)',
                    borderRadius: 3, color: 'rgba(255,255,255,.4)', cursor: 'pointer', fontSize: 13,
                  }}>✕</button>
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.3)', marginTop: 7 }}>
                  {t('auction.adjustAuthWarning')}
                </div>
              </div>
            )}
          </div>

          {/* Mandate status */}
          {selectedLot?.has_mandate && (
            <div style={{ padding: '12px 18px', borderBottom: '1px solid rgba(255,255,255,.06)', flexShrink: 0 }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,.35)', letterSpacing: 2, fontFamily: 'monospace', marginBottom: 8 }}>{t('auction.myMandateStatus')}</div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                background: 'rgba(43,92,230,.1)', border: '1px solid rgba(43,92,230,.2)', borderRadius: 3,
              }}>
                <span style={{ fontSize: 18 }}>{getIcon(selectedLot.category)}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {selectedLot.title_zh || '--'}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', fontFamily: 'monospace' }}>
                    {t('auction.authPrice')} {formatJPY(selectedLot.max_bid)} · {t('auction.engineStandby')}
                  </div>
                </div>
                <span style={{
                  fontSize: 11, padding: '3px 9px', borderRadius: 20,
                  background: 'rgba(46,125,79,.2)', color: '#5FC98A',
                }}>{t('auction.engineListening')}</span>
              </div>
            </div>
          )}

          {/* Bid log */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 18px' }}>
            <div style={{
              fontSize: 10, color: 'rgba(255,255,255,.3)', letterSpacing: 2, fontFamily: 'monospace',
              marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid rgba(255,255,255,.06)',
            }}>
              {t('auction.bidHistory')} <span style={{ color: 'rgba(255,255,255,.4)' }}>{bidHistory.length}</span>
            </div>
            {bidHistory.map((bid, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 3, marginBottom: 3,
                ...(bid.mine ? { background: 'rgba(43,92,230,.1)', border: '1px solid rgba(43,92,230,.15)' } : {}),
              }}>
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(255,255,255,.25)', width: 24, flexShrink: 0 }}>
                  #{bid.rank}
                </span>
                <span style={{ fontSize: 12, flex: 1 }}>
                  {bid.bidder}
                  {bid.mine && (
                    <span style={{
                      fontSize: 10, padding: '1px 6px', borderRadius: 2,
                      background: 'rgba(43,92,230,.2)', color: '#7BA7F7', marginLeft: 4,
                    }}>{t('auction.me')}</span>
                  )}
                </span>
                <span style={{
                  fontFamily: 'monospace', fontSize: 13, fontWeight: 500,
                  color: bid.rank === 1 ? GOLD : '#fff',
                }}>
                  {formatJPY(bid.price)}
                </span>
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(255,255,255,.25)', width: 56, textAlign: 'right', flexShrink: 0 }}>
                  {bid.time}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* -- Right: Engine status + Session info -- */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Engine status */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,.06)', flexShrink: 0 }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.35)', letterSpacing: 2, fontFamily: 'monospace', marginBottom: 8 }}>{t('auction.engineStatus')}</div>
            {[
              { key: t('auction.pageMonitor'), val: engineStatus?.page || '—', cls: engineStatus?.page ? 'ok' : '' },
              { key: 'Session', val: engineStatus?.session || '—', cls: engineStatus?.session ? 'ok' : '' },
              { key: t('auction.proxyIp'), val: engineStatus?.proxy || '—', cls: engineStatus?.proxy ? 'ok' : '' },
              { key: t('auction.bidTiming'), val: engineStatus?.timing || '—', cls: engineStatus?.timing ? 'warn' : '' },
              { key: t('auction.memoryUsage'), val: engineStatus?.memory || '—', cls: '' },
            ].map((row) => (
              <div key={row.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', fontSize: 12 }}>
                <span style={{ color: 'rgba(255,255,255,.4)' }}>{row.key}</span>
                <span style={{
                  fontFamily: 'monospace', fontSize: 11,
                  color: row.cls === 'ok' ? '#5FC98A' : row.cls === 'warn' ? GOLD : row.cls === 'err' ? RED : '#fff',
                }}>{row.val}</span>
              </div>
            ))}
          </div>

          {/* Session info */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,.06)', flexShrink: 0 }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.35)', letterSpacing: 2, fontFamily: 'monospace', marginBottom: 8 }}>{t('auction.currentSession')}</div>
            <div style={{ fontFamily: 'var(--serif,serif)', fontSize: 15, fontWeight: 600, marginBottom: 10 }}>
              {currentSession.house_name} · {currentSession.name}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {[
                { l: t('browse.auctionHouse'), v: currentSession.house_name || '--' },
                { l: t('auction.sessionType'), v: `${getIcon(currentSession.type)} ${currentSession.type === 'watch' ? t('browse.watches') : currentSession.type === 'jewelry' ? t('browse.jewelry') : t('auction.mixed')}` },
                { l: t('session.lotCount'), v: String(currentSession.lot_count || lots.length) },
                { l: t('auction.myMandates'), v: `${myMandates} ${t('common.items')}`, color: '#5FC98A' },
                { l: t('auction.traded'), v: String(doneLots) },
                { l: t('auction.bidding'), v: String(liveLots), color: GOLD },
              ].map(item => (
                <div key={item.l} style={{ background: 'rgba(255,255,255,.04)', borderRadius: 3, padding: '7px 9px' }}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,.3)', marginBottom: 3 }}>{item.l}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 11, color: item.color || '#fff' }}>{item.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Lots overview */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.35)', letterSpacing: 2, fontFamily: 'monospace', marginBottom: 10 }}>{t('auction.lotsOverview')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 12 }}>
              {[
                { val: lots.filter(l => l.has_mandate && l.status === 'done').length, label: t('auction.traded'), color: '#5FC98A' },
                { val: liveLots, label: t('auction.bidding'), color: GOLD },
                { val: lots.filter(l => l.status === 'soon').length, label: t('session.upcoming'), color: '#fff' },
              ].map(st => (
                <div key={st.label} style={{ background: 'rgba(255,255,255,.04)', borderRadius: 3, padding: '8px 6px', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 600, color: st.color }}>{st.val}</div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,.3)', marginTop: 2 }}>{st.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {lots.slice(0, 8).map(lot => (
                <div
                  key={lot.id}
                  onClick={() => setSelectedLot(lot)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                    background: selectedLot?.id === lot.id ? 'rgba(200,146,42,.08)' : 'rgba(255,255,255,.03)',
                    border: selectedLot?.id === lot.id ? '1px solid rgba(200,146,42,.15)' : '1px solid transparent',
                    borderRadius: 3, cursor: 'pointer',
                  }}
                >
                  <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(255,255,255,.25)', width: 20 }}>
                    {lot.lot_number || lot.id}
                  </span>
                  <span style={{
                    fontSize: 11, color: 'rgba(255,255,255,.75)', flex: 1,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {lot.title_zh || '--'}
                  </span>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                    background: lot.status === 'live' ? '#5FC98A' : lot.status === 'soon' ? GOLD : 'rgba(255,255,255,.2)',
                  }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes blink{0%,100%{opacity:1}50%{opacity:.2}}
        @keyframes urgentpulse{from{opacity:1}to{opacity:.6}}
      `}</style>
    </div>
  );
}
